// Shared labels and formatting for the health module. Tone rule (handoff
// Section 13): pastoral and plain, no league-table language, no pass or fail.

import type { HealthBand } from './api';

/** Respondent role enum (worker db.ts RESPONDENT_ROLES) with display labels. */
export const ROLE_LABELS: Record<string, string> = {
  clergy: 'Clergy',
  warden: 'Warden',
  parish_council: 'Parish Council',
  ministry_leader: 'Ministry Leader',
  staff: 'Staff',
  other: 'Other',
};

export const ROLE_ORDER = [
  'clergy',
  'warden',
  'parish_council',
  'ministry_leader',
  'staff',
  'other',
] as const;

export const roleLabel = (role: string | null | undefined): string =>
  role ? (ROLE_LABELS[role] ?? role) : '';

/** Invitation status pills (worker invitation.status enum). */
const INVITATION_STATUS_LABELS: Record<string, string> = {
  created: 'Not yet invited',
  invited: 'Invited',
  delivered: 'Delivered',
  opened: 'Opened',
  in_progress: 'In progress',
  submitted: 'Submitted',
  bounced: 'Bounced',
  declined: 'Declined',
  expired: 'Expired',
};

export const invitationStatusLabel = (status: string): string =>
  INVITATION_STATUS_LABELS[status] ?? status;

export const BAND_LABELS: Record<HealthBand, string> = {
  embedded: 'Embedded',
  strong: 'Strong',
  developing: 'Developing',
  priority: 'Priority',
};

export const CYCLE_STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  closed: 'Closed',
  debriefed: 'Debriefed',
  archived: 'Archived',
};

const nzDate = new Intl.DateTimeFormat('en-NZ', {
  dateStyle: 'long',
  timeZone: 'Pacific/Auckland',
});

const nzDateShort = new Intl.DateTimeFormat('en-NZ', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'Pacific/Auckland',
});

/** Long NZ date, e.g. "12 August 2026". Empty string when unknown. */
export function formatNzDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : nzDate.format(d);
}

/** Short NZ date, e.g. "12 Aug 2026". Empty string when unknown. */
export function formatNzDateShort(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : nzDateShort.format(d);
}

/** Distribution as plain evidence, e.g. "2 scored 4, 1 scored 3, 2 scored 1". */
export function distributionEvidence(distribution: {
  1: number;
  2: number;
  3: number;
  4: number;
}): string {
  const parts: string[] = [];
  for (const value of [4, 3, 2, 1] as const) {
    const n = distribution[value];
    if (n > 0) parts.push(`${n} scored ${value}`);
  }
  return parts.join(', ');
}

/** Parse pasted "name, email, role" lines into invitee rows. Lines missing a
 *  usable email are returned in `rejected` so the user can fix them. */
export function parseInviteeLines(input: string): {
  invitees: Array<{ name?: string; email: string; role?: string }>;
  rejected: string[];
} {
  const invitees: Array<{ name?: string; email: string; role?: string }> = [];
  const rejected: string[] = [];
  const roleFromToken = (token: string): string | undefined => {
    const norm = token
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');
    return ROLE_LABELS[norm] ? norm : undefined;
  };
  for (const rawLine of input.split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;
    const parts = line.split(/[,\t;]/).map((p) => p.trim());
    const email = parts.find((p) => p.includes('@'));
    if (!email) {
      rejected.push(line);
      continue;
    }
    const rest = parts.filter((p) => p !== email && p.length > 0);
    const role = rest.map(roleFromToken).find((r) => r !== undefined);
    const name = rest.find((p) => roleFromToken(p) === undefined);
    invitees.push({
      email,
      ...(name ? { name } : {}),
      ...(role ? { role } : {}),
    });
  }
  return { invitees, rejected };
}
